import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { platformSystemNotificationActionPathSchema, platformSystemNotificationConfigurationSchema, platformSystemNotificationCopySchema, platformSystemNotificationDisplaySchema, platformSystemNotificationEntrySchema, platformSystemNotificationsOperationsReadPermission, platformSystemNotificationsReadOwnPermission, } from '@crest/platform-contracts';
import { useCallback, useEffect, Fragment, useMemo, useRef, useState, } from 'react';
import { z } from 'zod';
import { usePlatformApi } from '../api/platform-api-provider.js';
import { usePlatformBootstrap } from '../runtime/platform-bootstrap-provider.js';
import { PlatformSystemNotificationApiClient } from './platform-system-notification.api-client.js';
export class PlatformSystemNotificationPollingCoordinator {
    #options;
    #configuration = null;
    #countTimer = null;
    #listTimer = null;
    #countAbort = null;
    #listAbort = null;
    #disposed = false;
    constructor(options) {
        this.#options = options;
    }
    update(configuration) {
        if (this.#disposed)
            return;
        const previous = this.#configuration;
        this.#configuration = configuration;
        const countChanged = previous === null ||
            previous.visible !== configuration.visible ||
            previous.unreadPollMilliseconds !== configuration.unreadPollMilliseconds;
        const listChanged = previous === null ||
            previous.open !== configuration.open ||
            previous.openListPollMilliseconds !== configuration.openListPollMilliseconds;
        if (countChanged) {
            this.#stopCount();
            if (configuration.visible) {
                void this.refreshCount();
                this.#countTimer = setInterval(() => {
                    void this.refreshCount();
                }, configuration.unreadPollMilliseconds);
            }
        }
        if (listChanged) {
            this.#stopList();
            if (configuration.open) {
                void this.refreshList();
                this.#listTimer = setInterval(() => {
                    void this.refreshList();
                }, configuration.openListPollMilliseconds);
            }
        }
    }
    async refreshCount() {
        if (this.#disposed || this.#configuration?.visible !== true)
            return;
        this.#countAbort?.abort();
        const abort = new AbortController();
        this.#countAbort = abort;
        this.#options.onCountStart?.();
        try {
            const result = await this.#options.loadCount(abort.signal);
            if (!abort.signal.aborted && this.#countAbort === abort)
                this.#options.onCount(result);
        }
        catch (error) {
            if (!abort.signal.aborted && this.#countAbort === abort)
                this.#options.onCountError?.(error);
        }
        finally {
            if (this.#countAbort === abort)
                this.#countAbort = null;
        }
    }
    async refreshList() {
        if (this.#disposed || this.#configuration?.open !== true)
            return;
        this.#listAbort?.abort();
        const abort = new AbortController();
        this.#listAbort = abort;
        this.#options.onListStart?.();
        try {
            const result = await this.#options.loadList(abort.signal);
            if (!abort.signal.aborted && this.#listAbort === abort)
                this.#options.onList(result);
        }
        catch (error) {
            if (!abort.signal.aborted && this.#listAbort === abort)
                this.#options.onListError?.(error);
        }
        finally {
            if (this.#listAbort === abort)
                this.#listAbort = null;
        }
    }
    dispose() {
        if (this.#disposed)
            return;
        this.#disposed = true;
        this.#stopCount();
        this.#stopList();
        this.#configuration = null;
    }
    #stopCount() {
        if (this.#countTimer !== null)
            clearInterval(this.#countTimer);
        this.#countTimer = null;
        this.#countAbort?.abort();
        this.#countAbort = null;
    }
    #stopList() {
        if (this.#listTimer !== null)
            clearInterval(this.#listTimer);
        this.#listTimer = null;
        this.#listAbort?.abort();
        this.#listAbort = null;
    }
}
export class PlatformSystemNotificationOperationsCoordinator {
    #options;
    #enabled = false;
    #needsLoad = true;
    #abort = null;
    #disposed = false;
    constructor(options) {
        this.#options = options;
    }
    update(enabled) {
        if (this.#disposed)
            return;
        const changed = this.#enabled !== enabled;
        this.#enabled = enabled;
        if (!enabled) {
            if (this.#abort !== null) {
                this.#abort.abort();
                this.#abort = null;
                this.#needsLoad = true;
                this.#options.onAbort();
            }
            return;
        }
        if (changed && this.#needsLoad)
            void this.refresh();
    }
    async refresh() {
        if (this.#disposed || !this.#enabled)
            return;
        this.#abort?.abort();
        const abort = new AbortController();
        this.#abort = abort;
        this.#needsLoad = false;
        this.#options.onStart();
        try {
            const health = await this.#options.load(abort.signal);
            if (!abort.signal.aborted && this.#abort === abort && this.#enabled) {
                this.#options.onSuccess(health);
            }
        }
        catch (error) {
            if (!abort.signal.aborted && this.#abort === abort && this.#enabled) {
                this.#options.onFailure(error);
            }
        }
        finally {
            if (this.#abort === abort)
                this.#abort = null;
        }
    }
    dispose() {
        if (this.#disposed)
            return;
        this.#disposed = true;
        this.#enabled = false;
        this.#abort?.abort();
        this.#abort = null;
    }
}
export class PlatformSystemNotificationCommandIds {
    #create;
    #markOne = new Map();
    #markAll = null;
    constructor(create = () => crypto.randomUUID()) {
        this.#create = create;
    }
    markOne(notificationId) {
        const retained = this.#markOne.get(notificationId);
        if (retained !== undefined)
            return retained;
        const created = this.#create();
        this.#markOne.set(notificationId, created);
        return created;
    }
    completeMarkOne(notificationId) {
        this.#markOne.delete(notificationId);
    }
    markAll() {
        this.#markAll ??= this.#create();
        return this.#markAll;
    }
    completeMarkAll() {
        this.#markAll = null;
    }
}
export class PlatformSystemNotificationCommandController {
    #api;
    #callbacks;
    #commands;
    #abort = null;
    constructor(api, callbacks, commands = new PlatformSystemNotificationCommandIds()) {
        this.#api = api;
        this.#callbacks = callbacks;
        this.#commands = commands;
    }
    async markOne(entry) {
        this.#abort?.abort();
        const abort = new AbortController();
        this.#abort = abort;
        this.#callbacks.setBusy(entry.id);
        this.#callbacks.setFailure(false);
        try {
            await this.#api.markOne(entry.id, this.#commands.markOne(entry.id), abort.signal);
            if (abort.signal.aborted)
                return false;
            await this.#callbacks.invalidate();
            if (abort.signal.aborted)
                return false;
            this.#commands.completeMarkOne(entry.id);
            return true;
        }
        catch {
            if (!abort.signal.aborted)
                this.#callbacks.setFailure(true);
            return false;
        }
        finally {
            if (this.#abort === abort)
                this.#abort = null;
            if (!abort.signal.aborted)
                this.#callbacks.setBusy(null);
        }
    }
    async markAll() {
        this.#abort?.abort();
        const abort = new AbortController();
        this.#abort = abort;
        this.#callbacks.setBusy('all');
        this.#callbacks.setFailure(false);
        try {
            await this.#api.markAll(this.#commands.markAll(), abort.signal);
            if (abort.signal.aborted)
                return false;
            await this.#callbacks.invalidate();
            if (abort.signal.aborted)
                return false;
            this.#commands.completeMarkAll();
            return true;
        }
        catch {
            if (!abort.signal.aborted)
                this.#callbacks.setFailure(true);
            return false;
        }
        finally {
            if (this.#abort === abort)
                this.#abort = null;
            if (!abort.signal.aborted)
                this.#callbacks.setBusy(null);
        }
    }
    async activate(entry, navigate) {
        if (entry.readAt === null)
            await this.markOne(entry);
        navigateToPlatformSystemNotification(entry.actionPath, navigate);
    }
    dispose() {
        this.#abort?.abort();
        this.#abort = null;
    }
}
export function platformSystemNotificationBadgeText(count) {
    if (!Number.isInteger(count) || count <= 0)
        return null;
    return count > 99 ? '99+' : String(count);
}
export function navigateToPlatformSystemNotification(actionPath, navigate) {
    const parsed = platformSystemNotificationActionPathSchema.safeParse(actionPath);
    if (!parsed.success)
        return false;
    navigate(parsed.data);
    return true;
}
function templateCount(value, count) {
    return value.replaceAll('{count}', String(count));
}
function focusableElements(container) {
    return Array.from(container.querySelectorAll([
        'a[href]',
        'button:not([disabled])',
        'input:not([disabled])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        '[tabindex]:not([tabindex="-1"])',
    ].join(','))).filter((element) => !element.hasAttribute('hidden'));
}
export function platformSystemNotificationWrappedFocus(focusable, active, shiftKey) {
    if (focusable.length === 0)
        return null;
    if (shiftKey && active === focusable[0])
        return focusable[focusable.length - 1];
    if (!shiftKey && active === focusable[focusable.length - 1])
        return focusable[0];
    return null;
}
export function platformSystemNotificationInitialFocus(focusable, panel) {
    return focusable[0] ?? panel;
}
export function restorePlatformSystemNotificationFocus(target) {
    if (target?.isConnected === true)
        target.focus();
}
export function isOutsidePlatformSystemNotificationDialog(panel, bell, target) {
    return !panel.contains(target) && bell?.contains(target) !== true;
}
export function shouldClosePlatformSystemNotificationDialog(key) {
    return key === 'Escape';
}
export function shouldRequestPlatformSystemNotificationOperations(canReadOperations, open) {
    return canReadOperations && open;
}
const notificationDayFormatter = new Intl.DateTimeFormat(undefined, { dateStyle: 'long' });
const notificationTimeFormatter = new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
});
function notificationDay(value) {
    return notificationDayFormatter.format(new Date(value));
}
function notificationTime(value) {
    return notificationTimeFormatter.format(new Date(value));
}
function PlatformSystemNotificationIcon() {
    return (_jsx("svg", { "aria-hidden": "true", viewBox: "0 0 24 24", children: _jsx("path", { d: "M12 2a6 6 0 0 0-6 6v3.4L4.4 15a1 1 0 0 0 .9 1.4h13.4a1 1 0 0 0 .9-1.4L18 11.4V8a6 6 0 0 0-6-6Zm0 20a3 3 0 0 0 2.8-2H9.2A3 3 0 0 0 12 22Z" }) }));
}
function PlatformSystemNotificationEntryItem(props) {
    return (_jsxs("li", { className: "crest-system-notification-entry", "data-density": props.display.density, "data-read": String(props.entry.readAt !== null), children: [_jsxs("a", { className: "crest-system-notification-entry-link", href: props.entry.actionPath, onClick: (event) => {
                    event.preventDefault();
                    props.onActivate(props.entry);
                }, children: [_jsx("strong", { children: props.entry.title }), _jsx("span", { children: props.entry.body }), props.display.showTimestamps ? (_jsx("time", { dateTime: props.entry.createdAt, children: notificationTime(props.entry.createdAt) })) : null] }), props.entry.readAt !== null ? null : (_jsx("button", { className: "crest-system-notification-read-action", disabled: props.commandBusy, type: "button", onClick: () => props.onMarkRead(props.entry), children: props.copy.markReadAction }))] }));
}
function NotificationList(props) {
    if (props.state.status === 'loading' || props.state.status === 'idle') {
        return _jsx("p", { "aria-live": "polite", children: props.copy.loadingLabel });
    }
    if (props.state.status === 'failed') {
        return (_jsxs("div", { role: "alert", className: "crest-stack", children: [_jsx("p", { children: props.copy.unavailableLabel }), _jsx("button", { type: "button", onClick: props.onRetry, children: props.copy.retryAction })] }));
    }
    if (props.state.entries.length === 0) {
        return (_jsxs("div", { className: "crest-system-notification-empty", role: "status", children: [_jsx("strong", { children: props.copy.emptyTitle }), _jsx("p", { children: props.copy.emptyDescription })] }));
    }
    let lastDay = '';
    return (_jsxs(_Fragment, { children: [props.state.status === 'partial' || props.state.status === 'retrying' ? (_jsxs("div", { className: "crest-system-notification-partial", role: "status", children: [_jsx("span", { children: props.state.status === 'retrying' ? props.copy.loadingLabel : props.copy.unavailableLabel }), props.state.status === 'partial' ? (_jsx("button", { type: "button", onClick: props.onRetry, children: props.copy.retryAction })) : null] })) : null, _jsx("ol", { className: "crest-system-notification-list", children: props.state.entries.map((entry) => {
                    const day = notificationDay(entry.createdAt);
                    const showDay = props.display.groupByDate && day !== lastDay;
                    lastDay = day;
                    return (_jsxs(Fragment, { children: [showDay ? _jsx("li", { className: "crest-system-notification-day", children: day }) : null, _jsx(PlatformSystemNotificationEntryItem, { commandBusy: props.commandBusyId === entry.id, copy: props.copy, display: props.display, entry: entry, onActivate: props.onActivate, onMarkRead: props.onMarkRead })] }, entry.id));
                }) })] }));
}
function OperationsStatus(props) {
    if (props.state.status === 'idle')
        return null;
    if (props.state.status === 'loading') {
        return _jsx("p", { className: "crest-muted", "aria-live": "polite", children: props.copy.loadingLabel });
    }
    if (props.state.status === 'failed' || props.state.health === null) {
        return (_jsxs("div", { className: "crest-system-notification-operations", role: "alert", children: [_jsx("p", { children: props.copy.unavailableLabel }), _jsx("button", { type: "button", onClick: props.onRefresh, children: props.copy.operationsRefreshAction })] }));
    }
    const statusCopy = props.state.health.status === 'attention'
        ? props.copy.operatorAttention
        : props.state.health.status === 'retrying'
            ? props.copy.automaticRetry
            : props.state.recovered
                ? props.copy.recovered
                : null;
    return (_jsxs("section", { className: "crest-system-notification-operations", "aria-label": props.copy.operationsStatusLabel, children: [statusCopy === null ? null : _jsx("p", { role: "status", children: statusCopy }), _jsxs("dl", { children: [_jsxs("div", { children: [_jsx("dt", { children: props.copy.operationsPendingLabel }), _jsx("dd", { children: props.state.health.pendingCount })] }), _jsxs("div", { children: [_jsx("dt", { children: props.copy.operationsRetryingLabel }), _jsx("dd", { children: props.state.health.retryingCount })] }), _jsxs("div", { children: [_jsx("dt", { children: props.copy.operationsInProgressLabel }), _jsx("dd", { children: props.state.health.leasedCount })] }), _jsxs("div", { children: [_jsx("dt", { children: props.copy.operationsNeedsAttentionLabel }), _jsx("dd", { children: props.state.health.deadLetterCount })] })] }), _jsx("button", { type: "button", onClick: props.onRefresh, children: props.copy.operationsRefreshAction })] }));
}
export function PlatformSystemNotificationBellView(props) {
    const badge = platformSystemNotificationBadgeText(props.unreadCount);
    const bellLabel = props.unreadCount > 0
        ? templateCount(props.copy.unreadLabel, props.unreadCount)
        : props.copy.bellLabel;
    const panelStyle = {
        '--crest-notification-panel-max-height': `${props.display.panelMaximumHeightPx}px`,
    };
    return (_jsxs("div", { className: "crest-system-notification-bell", children: [_jsxs("button", { ref: props.bellRef, "aria-controls": "crest-system-notification-panel", "aria-expanded": props.open, "aria-label": bellLabel, className: "crest-system-notification-bell-button", type: "button", onClick: props.onToggle, children: [_jsx(PlatformSystemNotificationIcon, {}), badge === null ? null : _jsx("span", { className: "crest-system-notification-badge", children: badge })] }), props.open ? (_jsxs("div", { ref: props.panelRef, "aria-labelledby": "crest-system-notification-title", "aria-modal": "true", className: "crest-system-notification-panel", id: "crest-system-notification-panel", role: "dialog", style: panelStyle, tabIndex: -1, children: [_jsxs("header", { className: "crest-system-notification-header", children: [_jsx("h2", { id: "crest-system-notification-title", children: props.copy.panelTitle }), _jsx("button", { "aria-label": props.copy.closeAction, className: "crest-system-notification-close", type: "button", onClick: props.onClose, children: _jsx("span", { "aria-hidden": "true", children: "\u00D7" }) })] }), props.countFailed ? (_jsxs("div", { className: "crest-system-notification-count-failure", role: "alert", children: [_jsx("p", { className: "crest-muted", children: props.copy.unavailableLabel }), _jsx("button", { type: "button", onClick: props.onRetryCount, children: props.copy.retryAction })] })) : null, props.unreadCount > 0 ? (_jsx("button", { className: "crest-system-notification-mark-all", disabled: props.commandBusyId !== null, type: "button", onClick: props.onMarkAll, children: props.copy.markAllAction })) : null, props.commandFailure ? (_jsx("p", { className: "crest-error-text", role: "alert", children: props.copy.commandFailure })) : null, _jsx(NotificationList, { commandBusyId: props.commandBusyId, copy: props.copy, display: props.display, state: props.listState, onActivate: props.onActivate, onMarkRead: props.onMarkRead, onRetry: props.onRetryList }), props.canReadOperations ? (_jsx(OperationsStatus, { copy: props.copy, state: props.operationsState, onRefresh: props.onRefreshOperations })) : null] })) : null] }));
}
function AuthorizedPlatformSystemNotificationBell(props) {
    const api = usePlatformApi();
    const notificationApi = useMemo(() => new PlatformSystemNotificationApiClient(api), [api]);
    const [open, setOpen] = useState(false);
    const [visible, setVisible] = useState(() => typeof document === 'undefined' || document.visibilityState === 'visible');
    const [unreadCount, setUnreadCount] = useState(0);
    const [countFailed, setCountFailed] = useState(false);
    const [listState, setListState] = useState({
        status: 'idle',
        entries: [],
        nextCursor: null,
    });
    const [operationsState, setOperationsState] = useState({
        status: 'idle',
        health: null,
        recovered: false,
    });
    const [commandBusyId, setCommandBusyId] = useState(null);
    const [commandFailure, setCommandFailure] = useState(false);
    const panelRef = useRef(null);
    const bellRef = useRef(null);
    const returnFocusRef = useRef(null);
    const loadCount = useCallback((signal) => notificationApi.unreadCount(signal), [notificationApi]);
    const loadList = useCallback((signal) => notificationApi.list({
        limit: props.configuration.initialPageSize,
        signal,
    }), [notificationApi, props.configuration.initialPageSize]);
    const polling = useMemo(() => new PlatformSystemNotificationPollingCoordinator({
        loadCount,
        loadList,
        onCountStart: () => setCountFailed(false),
        onCount: (count) => {
            setUnreadCount(count);
            setCountFailed(false);
        },
        onCountError: () => setCountFailed(true),
        onListStart: () => setListState((current) => ({
            ...current,
            status: current.status === 'failed' || current.status === 'partial'
                ? 'retrying'
                : current.entries.length === 0 ? 'loading' : current.status,
        })),
        onList: (result) => setListState({
            status: 'ready',
            entries: result.data,
            nextCursor: result.nextCursor,
        }),
        onListError: () => setListState((current) => ({
            ...current,
            status: current.entries.length === 0 ? 'failed' : 'partial',
        })),
    }), [loadCount, loadList]);
    useEffect(() => () => polling.dispose(), [polling]);
    useEffect(() => {
        polling.update({
            visible,
            open,
            unreadPollMilliseconds: props.configuration.unreadPollSeconds * 1_000,
            openListPollMilliseconds: props.configuration.openListPollSeconds * 1_000,
        });
    }, [open, polling, props.configuration.openListPollSeconds, props.configuration.unreadPollSeconds, visible]);
    useEffect(() => {
        if (typeof document === 'undefined')
            return undefined;
        const updateVisibility = () => setVisible(document.visibilityState === 'visible');
        document.addEventListener('visibilitychange', updateVisibility);
        return () => document.removeEventListener('visibilitychange', updateVisibility);
    }, []);
    const closePanel = useCallback(() => {
        setOpen(false);
        restorePlatformSystemNotificationFocus(returnFocusRef.current);
    }, []);
    useEffect(() => {
        if (!open || typeof document === 'undefined')
            return undefined;
        const panel = panelRef.current;
        if (panel === null)
            return undefined;
        const animationFrame = requestAnimationFrame(() => {
            platformSystemNotificationInitialFocus(focusableElements(panel), panel).focus();
        });
        const onPointerDown = (event) => {
            const target = event.target;
            if (!(target instanceof Node))
                return;
            if (isOutsidePlatformSystemNotificationDialog(panel, bellRef.current, target))
                closePanel();
        };
        const onKeyDown = (event) => {
            if (shouldClosePlatformSystemNotificationDialog(event.key)) {
                event.preventDefault();
                closePanel();
                return;
            }
            if (event.key !== 'Tab')
                return;
            const focusable = focusableElements(panel);
            if (focusable.length === 0) {
                event.preventDefault();
                panel.focus();
                return;
            }
            const wrapped = platformSystemNotificationWrappedFocus(focusable, document.activeElement instanceof HTMLElement ? document.activeElement : null, event.shiftKey);
            if (wrapped !== null) {
                event.preventDefault();
                wrapped.focus();
            }
        };
        document.addEventListener('pointerdown', onPointerDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            cancelAnimationFrame(animationFrame);
            document.removeEventListener('pointerdown', onPointerDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [closePanel, open]);
    const operations = useMemo(() => new PlatformSystemNotificationOperationsCoordinator({
        load: (signal) => notificationApi.operationsHealth(signal),
        onStart: () => setOperationsState((current) => ({ ...current, status: 'loading' })),
        onSuccess: (health) => setOperationsState((current) => ({
            status: 'ready',
            health,
            recovered: health.status === 'healthy' &&
                current.health !== null && current.health.status !== 'healthy',
        })),
        onFailure: () => setOperationsState((current) => ({ ...current, status: 'failed' })),
        onAbort: () => setOperationsState((current) => (current.status === 'loading' ? { ...current, status: 'idle' } : current)),
    }), [notificationApi]);
    useEffect(() => {
        operations.update(shouldRequestPlatformSystemNotificationOperations(props.canReadOperations, open));
    }, [open, operations, props.canReadOperations]);
    useEffect(() => () => operations.dispose(), [operations]);
    const invalidate = useCallback(async () => {
        await Promise.all([polling.refreshCount(), polling.refreshList()]);
    }, [polling]);
    const commandController = useMemo(() => new PlatformSystemNotificationCommandController(notificationApi, {
        invalidate,
        setBusy: setCommandBusyId,
        setFailure: setCommandFailure,
    }), [invalidate, notificationApi]);
    useEffect(() => () => commandController.dispose(), [commandController]);
    return (_jsx(PlatformSystemNotificationBellView, { bellRef: bellRef, canReadOperations: props.canReadOperations, commandBusyId: commandBusyId, commandFailure: commandFailure, copy: props.copy, countFailed: countFailed, display: props.display, listState: listState, open: open, operationsState: operationsState, panelRef: panelRef, unreadCount: unreadCount, onActivate: (entry) => void commandController.activate(entry, (path) => window.location.assign(path)), onClose: closePanel, onMarkAll: () => void commandController.markAll(), onMarkRead: (entry) => void commandController.markOne(entry), onRefreshOperations: () => void operations.refresh(), onRetryCount: () => void polling.refreshCount(), onRetryList: () => void polling.refreshList(), onToggle: () => {
            if (open) {
                closePanel();
            }
            else {
                returnFocusRef.current = document.activeElement instanceof HTMLElement
                    ? document.activeElement
                    : bellRef.current;
                setOpen(true);
            }
        } }));
}
export function PlatformSystemNotificationBell() {
    const bootstrap = usePlatformBootstrap();
    if (!bootstrap.permissions.includes(platformSystemNotificationsReadOwnPermission))
        return null;
    const configuration = platformSystemNotificationConfigurationSchema.parse(bootstrap.configuration.values['platform.system-notifications.configuration']);
    const display = platformSystemNotificationDisplaySchema.parse(bootstrap.configuration.values['platform.system-notifications.display']);
    const copy = platformSystemNotificationCopySchema.parse(bootstrap.configuration.values['platform.copy.system-notifications']);
    return (_jsx(AuthorizedPlatformSystemNotificationBell, { canReadOperations: bootstrap.permissions.includes(platformSystemNotificationsOperationsReadPermission), configuration: configuration, copy: copy, display: display }));
}
//# sourceMappingURL=platform-system-notification-bell.js.map